import { useMemo, useRef } from 'react'
import type { EChartsCoreOption } from 'echarts/core'
import { Pill } from '../controls/Pill'
import s from './DailyUsagePanels.module.css'
import { formatTimeBucket } from './chartFormat'
import {
  DAILY_LEGEND_SENTENCE, NOT_MEASURED, dailyPanelModel, environmentColour, environmentSwatch, mixColour,
  seatsLabel, type DailyPanelModel, type UsageEnvironment,
} from './usageChartModel'
import type * as Model from './usageChartModel'

/* Aliases beside the component (see TierUsageChart): a host types its data from `@solum/ui` alone. */
export type DailyUsageTier = Model.DailyUsageTier
export type DailyUsageDay = Model.DailyUsageDay
import { escapeHtml, tokenPx, useUsageEChart, type UsageInk } from './usageChartRuntime'

export interface DailyUsagePanelsProps {
  /** Per tier: per day, each environment's peak (drawn) and the counted tier usage. */
  tiers: readonly DailyUsageTier[]
  environments: readonly UsageEnvironment[]
  /** Environment keys to draw. Never changes a counted number. */
  visibleEnvironments?: readonly string[]
  testId?: string
}

export interface DailyPanelGeometry {
  left: number
  plotHeight: number
  rugGap: number
  rugHeight: number
  labelGap: number
}

const dayLabel = (date: string) => formatTimeBucket(date, 'day')
/* The axis speaks short dates ("27 Aug" / "Aug 27", the reader's locale): a
   DATE, pinned to UTC so no zone shifts it (F-67 clause 5). */
const shortDay = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
const axisDay = (date: string) => {
  const at = new Date(`${date}T00:00:00Z`)
  return Number.isNaN(at.getTime()) ? date : shortDay.format(at)
}

const list = (items: readonly string[]) => items.length < 3
  ? items.join(' and ')
  : `${items.slice(0, -1).join(', ')} and ${items.at(-1) ?? ''}`

/** The panel's option: day columns, one bar per drawn environment, the seats line and the over-seat rug. */
export function buildDailyPanelOption(
  panel: DailyPanelModel,
  environments: ReadonlyArray<UsageEnvironment & { shadeIndex: number }>,
  environmentCount: number,
  ink: UsageInk,
  geometry: DailyPanelGeometry,
  reducedMotion = false,
): EChartsCoreOption {
  const n = panel.days.length
  const multi = environments.length > 1
  const { top, ticks } = panel.scale
  const perPx = top / Math.max(1, geometry.plotHeight)
  const hatchBase = mixColour(ink.surface, ink.ink, 0.06)
  const hatchStripe = mixColour(ink.surface, ink.ink, 0.22)
  const rugTop = geometry.plotHeight + geometry.rugGap
  const shown = new Set([0, Math.round((n - 1) / 3), Math.round((2 * (n - 1)) / 3), n - 1])
  const dates = panel.days.map((d) => d.date)
  const hasRug = panel.seats !== null

  const series: Record<string, unknown>[] = environments.map((env, e) => ({
    type: 'bar',
    name: env.label,
    xAxisIndex: 0,
    yAxisIndex: 0,
    barGap: '12%',
    barCategoryGap: n > 45 ? '8%' : '22%',
    data: panel.days.map((day) => {
      const value = day.values[e] ?? null
      if (value === null) {
        return {
          value: 4 * perPx,
          itemStyle: {
            color: hatchBase,
            decal: { symbol: 'rect', symbolSize: 1, color: hatchStripe, dashArrayX: [1, 0], dashArrayY: [2, 2], rotation: -Math.PI / 4 },
          },
          label: { show: false },
        }
      }
      const colour = multi
        ? environmentColour(env.shadeIndex, environmentCount, ink)
        : day.over ? ink.warn : ink.brand
      const labelled = panel.showDayLabels && day.labelAt === e && day.usage !== null
      return {
        value: value === 0 ? 0.8 * perPx : value,
        itemStyle: { color: colour, borderRadius: [1.5, 1.5, 0, 0], decal: { symbol: 'none' } },
        label: labelled ? {
          show: true,
          position: 'top',
          distance: geometry.labelGap,
          formatter: String(day.usage),
          fontFamily: ink.font,
          fontSize: ink.sizeLabel,
          fontWeight: 'var(--fw-bold)',
          color: day.over ? ink.warn : ink.muted,
        } : { show: false },
      }
    }),
    ...(e === 0 && panel.seats !== null ? {
      markLine: {
        silent: true,
        symbol: ['none', 'none'],
        label: { show: false },
        animation: false,
        emphasis: { disabled: true },
        data: [{ yAxis: panel.seats, name: 'Seats', lineStyle: { type: 'solid', width: 2, color: ink.ink } }],
      },
    } : {}),
  }))

  if (hasRug) {
    series.push({
      type: 'bar',
      name: 'Over seats',
      xAxisIndex: 1,
      yAxisIndex: 1,
      silent: true,
      barCategoryGap: n > 45 ? '8%' : '22%',
      tooltip: { show: false },
      data: panel.days.map((day) => ({
        value: 1,
        itemStyle: { color: day.over ? ink.warn : 'transparent', borderRadius: 1, decal: { symbol: 'none' } },
      })),
    })
  }

  const tip = (index: number) => {
    const day = panel.days[index]
    if (!day) return ''
    const rows = environments.map((env, e) => {
      const v = day.values[e] ?? null
      const swatch = multi ? environmentColour(env.shadeIndex, environmentCount, ink) : ink.brand
      return `<div style="display:flex;gap:8px;align-items:center"><span style="width:8px;height:8px;border-radius:2px;background:${swatch}"></span>${escapeHtml(env.label)}<b style="margin-left:auto">${v === null ? NOT_MEASURED : v}</b></div>`
    }).join('')
    const setters = day.setBy.map((by) => (multi ? `${by.module} (${by.environment})` : by.module))
    const by = `Max ${escapeHtml(panel.label)} usage <b>${day.usage === null ? NOT_MEASURED : day.usage}</b>`
      + (panel.seats !== null ? ` of ${seatsLabel(panel.seats)}` : '')
      + (day.usage && setters.length ? ` · set by ${escapeHtml(list(setters))}` : '')
    return `<b>${escapeHtml(dayLabel(day.date))}</b>${rows}<div style="margin-top:4px">${by}</div>`
  }

  return {
    animation: !reducedMotion,
    animationDuration: reducedMotion ? 0 : 200,
    textStyle: { fontFamily: ink.font, color: ink.muted },
    aria: { enabled: true, decal: { show: true }, label: { enabled: false } },
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: { type: 'shadow', shadowStyle: { color: mixColour(ink.surface, ink.ink, 0.05) } },
      backgroundColor: ink.plate,
      borderColor: ink.hair,
      textStyle: { fontFamily: ink.font, color: ink.ink, fontSize: ink.tipSize },
      extraCssText: [
        ink.radius !== undefined ? `border-radius: ${ink.radius}px;` : '',
        ink.shadow ? `box-shadow: ${ink.shadow};` : '',
      ].join(' ').trim(),
      formatter: (params: Array<{ dataIndex: number }> | { dataIndex: number }) =>
        tip(Array.isArray(params) ? params[0]?.dataIndex ?? -1 : params.dataIndex),
    },
    grid: [
      { left: geometry.left, right: 0, top: 0, height: geometry.plotHeight, containLabel: false },
      ...(hasRug ? [{ left: geometry.left, right: 0, top: rugTop, height: geometry.rugHeight, containLabel: false }] : []),
    ],
    xAxis: [
      {
        gridIndex: 0,
        type: 'category',
        data: dates,
        axisTick: { show: false },
        axisLine: { show: true, lineStyle: { color: ink.border } },
        axisLabel: {
          interval: 0,
          margin: geometry.labelGap + (hasRug ? geometry.rugGap + geometry.rugHeight : 0),
          formatter: (value: string, index: number) => (shown.has(index) ? axisDay(value) : ''),
          fontFamily: ink.font,
          fontSize: ink.sizeLabel,
          fontWeight: 'var(--fw-semibold)',
          color: ink.muted,
        },
      },
      ...(hasRug ? [{ gridIndex: 1, type: 'category', data: dates, show: false }] : []),
    ],
    yAxis: [
      {
        gridIndex: 0,
        type: 'value',
        min: 0,
        max: top,
        interval: ticks.length > 2 ? top / 2 : top,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { margin: geometry.labelGap * 2, fontFamily: ink.font, fontSize: ink.sizeLabel, fontWeight: 'var(--fw-semibold)', color: ink.muted },
        splitLine: { show: true, lineStyle: { color: ink.hair } },
      },
      ...(hasRug ? [{ gridIndex: 1, type: 'value', min: 0, max: 1, show: false }] : []),
    ],
    series,
  } as EChartsCoreOption
}

function PanelChart({
  panel, environments, environmentCount,
}: {
  panel: DailyPanelModel
  environments: ReadonlyArray<UsageEnvironment & { shadeIndex: number }>
  environmentCount: number
}) {
  const host = useRef<HTMLDivElement>(null)
  const failure = useUsageEChart(host, ({ ink, read, height, reducedMotion }) => {
    const rugGap = tokenPx(read, '--sp-1', 4) - 1
    const rugHeight = tokenPx(read, '--sp-1', 4)
    const axis = tokenPx(read, '--sp-5', 20)
    return buildDailyPanelOption(panel, environments, environmentCount, ink, {
      left: tokenPx(read, '--sp-8', 32),
      plotHeight: Math.max(1, height - axis - rugGap - rugHeight),
      rugGap,
      rugHeight,
      labelGap: tokenPx(read, '--sp-0-5', 2),
    }, reducedMotion)
  }, [panel, environments, environmentCount], 'The daily usage chart could not be drawn.')
  const overText = panel.overDays === null ? '' : `, over seats on ${panel.overDays} of ${panel.dayCount} days`
  return (
    <>
      <div
        ref={host}
        className={s.plot}
        role={failure ? undefined : 'img'}
        aria-label={failure ? undefined : `${panel.label} daily peak per environment over ${panel.dayCount} days${overText}`}
        hidden={failure != null}
        data-testid="daily-usage-plot"
      />
      {failure ? <p role="alert" className={s.failure}>{failure}</p> : null}
    </>
  )
}

/**
 * THE DAILY USAGE HISTORY (Spec 8.5b): a Pro panel, then a Free panel; one bar
 * per environment per day; the day's counted peak rides its tallest bar; a rust
 * over-seat rug under the days whose COUNTED usage exceeded seats.
 */
export function DailyUsagePanels({ tiers, environments, visibleEnvironments, testId }: DailyUsagePanelsProps) {
  const drawn = useMemo(() => {
    const all = environments.map((env, shadeIndex) => ({ ...env, shadeIndex }))
    const keep = visibleEnvironments ? all.filter((env) => visibleEnvironments.includes(env.key)) : all
    return keep.length ? keep : all.slice(0, 1)
  }, [environments, visibleEnvironments])
  const panels = useMemo(() => [...tiers]
    .sort((a, b) => (a.tone === b.tone ? 0 : a.tone === 'pro' ? -1 : 1))
    .map((tier) => dailyPanelModel(tier, drawn)), [tiers, drawn])
  const count = environments.length

  return (
    <div className={s.root} data-testid={testId}>
      {panels.map((panel) => (
        <section key={panel.key} className={s.panel} data-tone={panel.tone} aria-label={`${panel.label} daily usage`} data-testid="daily-usage-panel">
          <header className={s.head}>
            <Pill tone={panel.tone === 'pro' ? 'brand' : 'neutral'}>{panel.label.toUpperCase()}</Pill>
            <span className={s.stat}>
              Peak <b>{panel.peak === null ? NOT_MEASURED : panel.peak}</b>
              {panel.peakDate ? ` on ${dayLabel(panel.peakDate)}` : ''}
            </span>
            <span className={s.stat}>
              {panel.overDays === null
                ? 'No seat limit'
                : <>Over seats on <b>{panel.overDays}</b> of {panel.dayCount} days</>}
            </span>
            {panel.seats !== null ? (
              <span className={s.key}><i aria-hidden="true" />{seatsLabel(panel.seats)}</span>
            ) : null}
          </header>
          <PanelChart panel={panel} environments={drawn} environmentCount={count} />
        </section>
      ))}
      <p className={s.legend}>
        {drawn.map((env) => (
          <span key={env.key} className={s.legendItem}>
            <i className={s.swatch} style={{ background: drawn.length > 1 ? environmentSwatch(env.shadeIndex, count) : 'var(--brand)' }} aria-hidden="true" />
            {env.label}
          </span>
        ))}
        <span className={s.legendItem}><i className={s.keySeat} aria-hidden="true" />Seats you have</span>
        <span className={s.legendItem}><i className={s.keyRug} aria-hidden="true" />Day over seats</span>
        <span className={s.legendItem}><i className={s.keyNone} aria-hidden="true" />Not measured</span>
        <span className={s.note}>{DAILY_LEGEND_SENTENCE}</span>
      </p>
    </div>
  )
}
